/**
 * URL helper utilities for handling slug-based and name-based URLs
 */

/**
 * Czech/Slovak character map — must match backend's generateSlug() in slug-utils.js
 * Stripping diacritics before regex ensures "Černý" → "cerny" not "-ern-"
 */
const CHAR_MAP = {
  'á': 'a', 'ä': 'a', 'č': 'c', 'ď': 'd', 'é': 'e', 'ě': 'e',
  'í': 'i', 'ľ': 'l', 'ĺ': 'l', 'ň': 'n', 'ó': 'o', 'ö': 'o',
  'ô': 'o', 'ŕ': 'r', 'ř': 'r', 'š': 's', 'ť': 't', 'ú': 'u',
  'ů': 'u', 'ü': 'u', 'ý': 'y', 'ž': 'z'
};

/**
 * Generate a URL-safe slug from text, matching backend's generateSlug() logic
 * @param {string} text - Text to slugify
 * @returns {string} URL-safe slug
 */
function generateSlug(text) {
  if (!text) return '';
  let slug = text.toLowerCase();
  // Replace Czech/Slovak diacritics BEFORE stripping non-ASCII chars
  slug = slug.split('').map(char => CHAR_MAP[char] || char).join('');
  slug = slug.replace(/[^a-z0-9]+/g, '-');
  slug = slug.replace(/^-+|-+$/g, '');
  slug = slug.replace(/-+/g, '-');
  return slug;
}

/**
 * Generate a category URL using slug if available, otherwise use name
 * @param {Object} category - Category object with name and optional slug
 * @returns {string} - Category URL path
 */
export const getCategoryUrl = (category) => {
  if (!category) return '/';

  // Prefer slug over name for SEO-friendly URLs
  const identifier = category.slug || category.name;
  return `/category/${encodeURIComponent(identifier)}`;
};

/**
 * Generate a brand URL using slug if available, otherwise use name
 * @param {Object} brand - Brand object with name and optional slug
 * @returns {string} - Brand URL path
 */
export const getBrandUrl = (brand) => {
  if (!brand) return '/';

  // Prefer slug over name for SEO-friendly URLs
  const identifier = brand.slug || brand.name;
  return `/brand/${encodeURIComponent(identifier)}`;
};

/**
 * Generate a product URL using slug if available, otherwise use ID
 * @param {Object} product - Product object with id and optional slug
 * @returns {string} - Product URL path
 */
export const getProductUrl = (product) => {
  if (!product) return '/';

  // Prefer slug over ID for SEO-friendly URLs
  const identifier = product.slug || product.id;
  return `/product/${encodeURIComponent(identifier)}`;
};

/**
 * Generate a vendor URL using slug if available, otherwise use ID
 * @param {Object} vendor - Vendor object with id/slug and optional name
 * @returns {string} - Vendor URL path
 */
export const getVendorUrl = (vendor) => {
  if (!vendor) return '/';

  // Prefer slug over ID for SEO-friendly URLs
  const identifier = vendor.slug || vendor.id;
  return `/vendor/${encodeURIComponent(identifier)}`;
};

/**
 * Generate a click tracking URL for an offer
 * Routes through the backend /click/ endpoint to track the click
 * before redirecting to the affiliate URL
 * @param {number|string} offerId - The offer ID
 * @param {Object} [product] - Optional product with brand and name for SEO-friendly slug URL
 * @returns {string} - Click tracking URL path
 */
export const getClickTrackingUrl = (offerId, product) => {
  if (!offerId) return '#';

  if (product?.brand && product?.name) {
    // Generate slug matching backend's generateProductSlug logic (uses diacritics-aware generateSlug)
    const brandSlug = generateSlug(product.brand);
    const nameSlug = generateSlug(product.name);
    const slug = brandSlug ? `${brandSlug}-${nameSlug}` : nameSlug;
    return `/api/v1/public/click/${slug}/${offerId}`;
  }

  // Fallback to legacy endpoint (backend redirects to slug-based URL)
  return `/api/v1/public/click/${offerId}`;
};
